import React, { useState } from 'react';
import { Tabs, Tab, Box, Typography, Button } from '@mui/material';
import SpinnerModal from './SpinnerModal';
import jsPDF from 'jspdf';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const Documentation = () => {
  const [tabIndex, setTabIndex] = useState(0);
  const [documentationFileName, setDocumentationFileName] = useState('');
  const [documentationFile, setDocumentationFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [showSpinnerModal, setShowSpinnerModal] = useState(false);
  const [apiResult, setApiResult] = useState(null);
  const [isFinalSubmit, setIsFinalSubmit] = useState(false);
  

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  // File upload handler
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      setDocumentationFileName(file.name);
      setDocumentationFile(file);

      // Upload the file to S3
      const formData = new FormData();
      formData.append('file', file);

      try {
        await fetch('https://api.elevate.de.ltim.io/file_upload_for_tuning_and_suggestion/', {
          method: 'POST',
          body: formData,
        });
      } catch (error) {
        alert(`Error uploading ${file.name}: ${error.message}`);
      }
    }
  };

  // Download PDF handler
  const handleDownloadPDF = () => {
    if (!apiResult || !apiResult.report) {
      alert('No API result available to download.');
      return;
    }
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text('Output', 10, 10);
    const resultContent = String(apiResult.report);
    doc.setFontSize(11);
    const pageWidth = doc.internal.pageSize.width;
    const margin = 10;
    const contentWidth = pageWidth - margin * 2;
    const lines = doc.splitTextToSize(resultContent, contentWidth);
    let cursorY = 20;
    const lineHeight = 7;
    const pageHeight = doc.internal.pageSize.height;
    lines.forEach((line) => {
      if (cursorY + lineHeight > pageHeight - margin) {
        doc.addPage();
        cursorY = margin;
      }
      doc.text(line, margin, cursorY);
      cursorY += lineHeight;
    });
    doc.save('Output.pdf');
  };

  // Copy code handler
  const handleCopyCode = () => {
    if (!apiResult || !apiResult.report) {
      alert('No API result available to copy.');
      return;
    }
    const resultContent = String(apiResult.report);
    navigator.clipboard.writeText(resultContent).then(() => {
    //   alert('Result content copied to clipboard!');
    }).catch((error) => {
      console.error('Failed to copy text:', error);
    });
  };

  // Submit handler: send payload to /process-tasks/
  const handleSubmit = async () => {
    if (!documentationFileName) return;

    setIsFinalSubmit(true);
    setShowSpinnerModal(true);
    setProgress(0);

    // Simulate progress up to 90%
    let interval = null;
    interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 90) {
          clearInterval(interval);
          return 90;
        }
        return prev + 10;
      });
    }, 10000);

    const payload = {
      type_of_metadata: "",
      file_name: documentationFileName,
      is_documentation: true,
    };

    try {
      const response = await fetch('https://api.elevate.de.ltim.io/tuning_and_suggestion_task_process/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      clearInterval(interval);
      setProgress(100); // Set to 100% on response

      if (!response.ok) {
        const errorData = await response.json();
        alert(`Failed to process documentation: ${errorData.detail}`);
        setShowSpinnerModal(false);
      } else {
        const data = await response.json();
        setApiResult(data);
        // Show success message for 2 seconds, then switch tab
        setTimeout(() => {
          setShowSpinnerModal(false);
          setTabIndex(1);
        }, 2000);
      }
    } catch (error) {
      clearInterval(interval);
      setShowSpinnerModal(false);
      alert(`Error processing documentation: ${error.message}`);
    }
  };

  return (
    <Box sx={{ width: '100%', marginTop: -7 }}>
      <Tabs
        value={tabIndex}
        onChange={handleTabChange}
        sx={{
          pl: 0,
          '& .MuiTabs-flexContainer': { justifyContent: 'flex-start' },
          '& .MuiTab-root': {
            minWidth: 120,
            fontWeight: 'bold',
            textTransform: 'none',
            paddingRight: 0,
            pl: 0,
          },
        }}
        TabIndicatorProps={{ style: { display: 'none' } }}
      >
        <Tab
          label={
            <Box display="flex" alignItems="center">
              {/* 1 Input */}
              <Box display="flex" alignItems="center">
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  width={24}
                  height={24}
                  borderRadius="50%"
                  bgcolor={apiResult?.report ? "#4caf50" : (tabIndex === 0 ? "#1976d2" : "white")}
                  border="1.5px solid"
                  borderColor={apiResult?.report ? "#4caf50" : (tabIndex === 0 ? "#1976d2" : "black")}
                  mr={1}
                  transition="all 0.2s"
                >
                  {apiResult?.report ? (
                    <CheckCircleIcon sx={{ color: "white", fontSize: 20 }} />
                  ) : (
                    <Typography variant="subtitle2" sx={{ color: tabIndex === 0 ? "white" : "black", fontWeight: 600 }}>1</Typography>
                  )}
                </Box>
                <Typography
                  sx={{
                    fontWeight: tabIndex === 0 ? 700 : 400,
                    color: tabIndex === 0 ? "primary.main" : "black",
                    userSelect: "none",
                    transition: "all 0.2s"
                  }}
                >
                  Input
                </Typography>
              </Box>
              {/* Arrow */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  height: 4,
                  ml: 1,
                  mr: 1,
                  mb: 0.6,
                }}
              >
                <Typography sx={{ color: "#bdbdbd", fontWeight: 700, fontSize: 44, lineHeight: 1 }}>
                  &rarr;
                </Typography>
              </Box>
            </Box>
          }
          disableRipple
          style={{ minWidth: 0, paddingRight: 0, paddingLeft: 0 }}
        />
        <Tab
          label={
            <Box display="flex" alignItems="center">
              {/* 2 Result */}
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                width={24}
                height={24}
                borderRadius="50%"
                bgcolor={tabIndex === 1 ? "#1976d2" : "white"}
                border="1.5px solid"
                borderColor={tabIndex === 1 ? "#1976d2" : "black"}
                mr={1}
                transition="all 0.2s"
              >
                <Typography variant="subtitle2" sx={{ color: tabIndex === 1 ? "white" : "black", fontWeight: 600 }}>2</Typography>
              </Box>
              <Typography
                sx={{
                  fontWeight: tabIndex === 1 ? 700 : 400,
                  color: tabIndex === 1 ? "primary.main" : "black",
                  userSelect: "none",
                  transition: "all 0.2s"
                }}
              >
                Result
              </Typography>
            </Box>
          }
          disableRipple
          style={{ minWidth: 0, paddingLeft: 0 }}
        />
      </Tabs>

      {tabIndex === 0 && (
        <Box sx={{ marginTop: 2 }}>
          <Typography variant="body1" sx={{ fontWeight: 'bold', marginBottom: 1 }}>
            Please upload file for generating Documentation
          </Typography>
          <Button
            variant="contained"
            color="primary"
            component="label"
            sx={{
              backgroundColor: 'white',
              color: 'black',
              border: '1px solid black',
              boxShadow: 'none',
              height: '40px',
            }}
          >
            Upload File
            <input
              type="file"
              hidden
              onChange={handleFileUpload}
            />
          </Button>
          {/* Display the uploaded file name and Submit button only after file is uploaded */}
          {documentationFileName && (
            <>
              <Typography
                variant="body2"
                sx={{ marginTop: 1, color: 'green', fontWeight: 'bold' }}
              >
                Uploaded File: {documentationFileName}
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                sx={{
                  marginTop: 2,
                  backgroundColor: 'white',
                  color: 'black',
                  border: '1px solid black',
                  boxShadow: 'none',
                  height: '40px',
                }}
              >
                Submit
              </Button>
            </>
          )}
        </Box>
      )}

      {tabIndex === 1 && isFinalSubmit && (
        <Box sx={{ marginTop: 4 }}>
          <Box
            sx={{
              marginTop: 4,
              padding: '16px',
              backgroundColor: '#f9f9f9',
              borderRadius: '8px',
              border: '1px solid #ccc',
              marginBottom: 2,
            }}
          >
            <Typography variant="h6" sx={{ marginBottom: 2 }}>
              Output
            </Typography>
            {apiResult?.report ? (
              <Typography
                variant="body1"
                sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}
              >
                {String(apiResult.report)}
              </Typography>
            ) : (
              <Typography variant="body2">No result available</Typography>
            )}
          </Box>
          {/* Download PDF and Copy Code buttons */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-start',
              gap: 2,
              marginTop: 2,
            }}
          >
            <Button
              variant="contained"
              color="primary"
              onClick={handleDownloadPDF}
              sx={{
                backgroundColor: 'white',
                color: 'black',
                border: '1px solid black',
                boxShadow: 'none',
              }}
              startIcon={<DownloadIcon />}
            >
              Download PDF
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleCopyCode}
              sx={{
                backgroundColor: 'white',
                color: 'black',
                border: '1px solid black',
                boxShadow: 'none',
              }}
              startIcon={<ContentCopyIcon />}
            >
              Copy Code
            </Button>
          </Box>
        </Box>
      )}

      <SpinnerModal
        open={showSpinnerModal}
        progress={progress}
        inProgressMessage="Documentation is being generated..."
        successMessage="Documentation generated successfully!"
        onClose={() => setShowSpinnerModal(false)}
      />
    </Box>
  );
};

export default Documentation;